'use client';
import { useEffect, useState } from 'react';
 import Link from'next/link';
 import Header from'@/components/common/Header';
 import Footer from'@/components/common/Footer';
 import Icon from'@/components/ui/AppIcon';

const coreTechnologies = [
  {
    id: 'tech_abap',
    name: 'ABAP',
    description: 'Advanced Business Application Programming',
    capabilities: ['Custom development', 'Business logic', 'Extensions', 'Integrations'],
    context: 'Core programming language for S/4HANA customizations and enhancements',
  },
  {
    id: 'tech_fiori',
    name: 'SAP Fiori',
    description: 'User experience and interface framework',
    capabilities: ['Responsive UX', 'Role-based apps', 'Mobile support', 'Launchpad'],
    context: 'Modern UI layer replacing traditional SAP GUI transactions',
  },
  {
    id: 'tech_hana',
    name: 'SAP HANA',
    description: 'In-memory database platform',
    capabilities: ['Real-time analytics', 'Transactional processing', 'Predictive analytics', 'Spatial processing'],
    context: 'Mandatory database for S/4HANA - enables simplified data models',
  },
  {
    id: 'tech_btp',
    name: 'SAP BTP',
    description: 'Business Technology Platform',
    capabilities: ['Cloud extensions', 'Integration', 'Analytics', 'AI/ML services'],
    context: 'PaaS for extending S/4HANA with cloud-native capabilities',
  },
  {
    id: 'tech_api',
    name: 'APIs (REST/OData)',
    description: 'Application programming interfaces',
    capabilities: ['External integration', 'Mobile apps', 'Headless architecture', 'Third-party access'],
    context: 'Standard interfaces for system-to-system communication',
  },
  {
    id: 'tech_cds',
    name: 'Core Data Services',
    description: 'Semantic data modeling',
    capabilities: ['Virtual data models', 'Annotations', 'Associations', 'Analytics'],
    context: 'HANA-native data modeling replacing traditional database views',
  },
]

const architecturePatterns = [
  {
    id: 'pattern_extension',
    name: 'Extension Patterns',
    description: 'Modify standard functionality without core changes',
    techniques: [
      { name: 'BAdIs', use: 'Business Add-Ins for custom logic injection' },
      { name: 'Enhancement Spots', use: 'Explicit enhancement points in standard code' },
      { name: 'Implicit Enhancement', use: 'Add code at predefined points' },
      { name: 'Side-by-Side Extensions', use: 'Cloud-based extensions via BTP' },
    ],
  },
  {
    id: 'pattern_integration',
    name: 'Integration Patterns',
    description: 'Connect S/4HANA with external systems',
    techniques: [
      { name: 'API-First', use: 'RESTful APIs for real-time integration' },
      { name: 'Event-Driven', use: 'Asynchronous messaging via Event Mesh' },
      { name: 'Batch Integration', use: 'IDocs for bulk data transfer' },
      { name: 'Direct RFC', use: 'Synchronous function calls (legacy)' },
    ],
  },
  {
    id: 'pattern_data',
    name: 'Data Patterns',
    description: 'Organize and access data efficiently',
    techniques: [
      { name: 'Universal Journal', use: 'Single source of truth for financial data' },
      { name: 'CDS Views', use: 'Virtual data models for analytics' },
      { name: 'Data Aging', use: 'Archive historical data to secondary storage' },
      { name: 'Master Data Governance', use: 'Centralized master data management' },
    ],
  },
]

const deploymentModels = [
  {
    id: 'deploy_onprem',
    name: 'On-Premise',
    description: 'Self-hosted in company data center',
    pros: ['Full control', 'Custom infrastructure', 'Existing investments'],
    cons: ['High upfront cost', 'Maintenance burden', 'Slower updates'],
    useCase: 'Organizations with strict data sovereignty or existing infrastructure',
  },
  {
    id: 'deploy_cloud',
    name: 'Cloud (Public/Private)',
    description: 'SAP-managed or hyperscaler-hosted',
    pros: ['Lower TCO', 'Automatic updates', 'Scalability', 'Faster deployment'],
    cons: ['Less customization', 'Internet dependency', 'Data residency concerns'],
    useCase: 'Greenfield implementations or cloud-first strategies',
  },
  {
    id: 'deploy_hybrid',
    name: 'Hybrid',
    description: 'Mix of on-premise and cloud components',
    pros: ['Flexibility', 'Gradual migration', 'Best of both worlds'],
    cons: ['Complex integration', 'Higher management overhead', 'Network dependencies'],
    useCase: 'Large enterprises with diverse requirements and phased transformations',
  },
]

export default function TechnicalPlaybookPage() {
  const [expandedPattern, setExpandedPattern] = useState<string | null>(null)

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal')
    if (!reveals.length) return

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    )
    reveals.forEach(el => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <main className="pt-24">
        {/* Page Header */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="reveal">
            <h1 className="font-serif text-display-md md:text-display-lg font-bold mb-4">
              Technical Playbook
            </h1>
            <p className="font-mono text-lg text-muted-foreground max-w-3xl">
              Architecture patterns, technologies, and implementation frameworks. How S/4HANA is built and extended.
            </p>
          </div>
        </section>

        {/* Core Technologies */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Core Technologies</h2>
            <p className="font-mono text-sm text-muted-foreground">Technical stack and capabilities</p>
          </div>

          <div className="overflow-x-auto reveal">
            <table className="table-research">
              <thead>
                <tr>
                  <th>Technology</th>
                  <th>Description</th>
                  <th>Key Capabilities</th>
                  <th>Context</th>
                </tr>
              </thead>
              <tbody>
                {coreTechnologies.map((tech) => (
                  <tr key={tech.id}>
                    <td className="font-mono font-semibold">{tech.name}</td>
                    <td className="font-mono text-sm">{tech.description}</td>
                    <td className="font-mono text-sm">
                      <ul className="space-y-1">
                        {tech.capabilities.map((cap, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="w-1 h-1 bg-primary rounded-full"></span>
                            {cap}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="font-mono text-sm text-muted-foreground">{tech.context}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Architecture Patterns */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-b border-border">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Architecture Patterns</h2>
            <p className="font-mono text-sm text-muted-foreground">Common implementation approaches</p>
          </div>

          <div className="space-y-4">
            {architecturePatterns.map((pattern) => (
              <div key={pattern.id} className="border border-border rounded reveal">
                <button
                  onClick={() => setExpandedPattern(expandedPattern === pattern.id ? null : pattern.id)}
                  className="w-full p-6 text-left hover:bg-muted transition-colors flex items-center justify-between"
                >
                  <div>
                    <h3 className="font-serif text-xl font-semibold mb-1">{pattern.name}</h3>
                    <p className="font-mono text-sm text-muted-foreground">{pattern.description}</p>
                  </div>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className={`text-muted-foreground transition-transform ${
                      expandedPattern === pattern.id ?'rotate-180' : ''
                    }`}
                    variant="outline"
                  />
                </button>

                {expandedPattern === pattern.id && (
                  <div className="px-6 pb-6 border-t border-border pt-6">
                    <div className="space-y-4">
                      {pattern.techniques.map((technique, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="w-2 h-2 bg-primary rounded-full mt-1.5 shrink-0"></div>
                          <div>
                            <h4 className="font-mono font-semibold mb-1">{technique.name}</h4>
                            <p className="font-mono text-sm text-muted-foreground">{technique.use}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Deployment Models */}
        <section className="mx-auto max-w-7xl px-6 py-16">
          <div className="mb-10 reveal">
            <h2 className="font-serif text-3xl font-semibold mb-2">Deployment Models</h2>
            <p className="font-mono text-sm text-muted-foreground">Infrastructure options and trade-offs</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {deploymentModels.map((model) => (
              <div key={model.id} className="border border-border rounded p-6 reveal">
                <h3 className="font-serif text-xl font-semibold mb-2">{model.name}</h3>
                <p className="font-mono text-sm text-muted-foreground mb-6">{model.description}</p>

                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="font-mono text-xs uppercase text-success mb-2">Advantages</h4>
                    <ul className="space-y-1">
                      {model.pros.map((pro, idx) => (
                        <li key={idx} className="font-mono text-sm flex items-start gap-2">
                          <Icon name="CheckIcon" size={16} className="text-success mt-0.5" variant="outline" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-mono text-xs uppercase text-warning mb-2">Considerations</h4>
                    <ul className="space-y-1">
                      {model.cons.map((con, idx) => (
                        <li key={idx} className="font-mono text-sm flex items-start gap-2">
                          <Icon name="ExclamationTriangleIcon" size={16} className="text-warning mt-0.5" variant="outline" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <h4 className="font-mono text-xs uppercase text-muted-foreground mb-2">Best For</h4>
                  <p className="font-mono text-sm">{model.useCase}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Navigation CTA */}
        <section className="mx-auto max-w-7xl px-6 py-16 border-t border-border">
          <div className="text-center space-y-6 reveal">
            <p className="font-mono text-muted-foreground">
              Plan your migration approach
            </p>
            <Link
              href="/migration-paths"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground font-mono text-sm rounded hover:opacity-90 transition-opacity"
            >
              Migration Paths
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}